import { DateTime } from 'luxon';

import { IPatient, JobStatus } from '../../../..';
import { getHcpAttributes } from './getHcpAttributes';

export type PersonJobStatusTemplate = {
    date: string;
    status: JobStatus;
    reason?: string;
    note?: string;
    followUpAction?: string | null;
    isReverse?: boolean;
};

export const getJobStatuses = (
    job: IPatient,
    options: { isBuddy?: boolean } = {},
): PersonJobStatusTemplate[] => {
    const {
        arrivedDateTime,
        madeCurrentDateTime,
        finishedDateTime,
        hcpAbortedDateTime,
        jobStatus,
        hcpAbortedNotes,
        hcpAbortedReason,
        controllerAbortedNotes,
        controllerAbortedReason,
        controllerReverseNotes,
        controllerReverseReason,
        hcpReverseReason,
        hcpReverseNotes,
        jobStatusBeforeAbort,
        withdrawnReason,
        withdrawnNotes,
    } = getHcpAttributes(job, options);

    const { lastUpdatedDateTime, createDateTime, abortedFollowUpAction } = job;

    // Create an array of job statuses, based on the v1 job, that will later be mapped to
    // PersonJobStatuses
    const jobStatuses: PersonJobStatusTemplate[] = [];

    // Check if there's been a CURRENT status
    if (madeCurrentDateTime) {
        jobStatuses.push({ date: madeCurrentDateTime, status: JobStatus.CURRENT });
    }

    // Check if there's been an ARRIVED status
    if (arrivedDateTime) {
        jobStatuses.push({ date: arrivedDateTime, status: JobStatus.ARRIVED });
    }

    // Check if there's been a HCP_ABORTED status
    if (hcpAbortedDateTime) {
        jobStatuses.push({
            date: hcpAbortedDateTime,
            status: JobStatus.HCP_ABORTED,
            reason: hcpAbortedReason,
            note: hcpAbortedNotes,
            followUpAction: abortedFollowUpAction,
        });
    }

    // Check if the job has been finished. COMPLETED and CONTROLLER_ABORTED both use
    // 'finishedDateTime', so if there's a finishedDateTime status but the job is not aborted, we
    // infer that it's COMPLETED
    if (finishedDateTime && jobStatus === JobStatus.CONTROLLER_ABORTED) {
        jobStatuses.push({
            date: finishedDateTime,
            status: JobStatus.CONTROLLER_ABORTED,
            reason: controllerAbortedReason,
            note: controllerAbortedNotes,
            followUpAction: abortedFollowUpAction,
        });
    } else if (finishedDateTime) {
        jobStatuses.push({ date: finishedDateTime, status: JobStatus.COMPLETED });
    }

    // Sort the statuses by most recent first
    jobStatuses.sort(({ date: timestampA }, { date: timestampB }) =>
        timestampB.localeCompare(timestampA),
    );

    let currentJobStatus = jobStatuses[0];

    // If using the timestamps to build the status history hasn't resulted in the latest status
    // matching the current one, we need to ensure that it's added to the start of the array
    if (currentJobStatus?.status !== jobStatus) {
        // First check if the current status is in the array but not at the start
        const currentJobStatusItem = jobStatuses.find(({ status }) => status === jobStatus);

        // If it is, remove it from its current position
        if (currentJobStatusItem) {
            const index = jobStatuses.indexOf(currentJobStatusItem);
            jobStatuses.splice(index, 1);
        }

        // Add the current status to the start of the array, or create it if it doesn't exist
        const newJobStatus = currentJobStatusItem || {
            date: lastUpdatedDateTime || createDateTime,
            status: jobStatus,
        };

        // Add any relevant reason or note attributes to the current status
        if (newJobStatus.status === JobStatus.WITHDRAWN) {
            newJobStatus.reason = withdrawnReason;
            newJobStatus.note = withdrawnNotes;
        } else if (newJobStatus.status === JobStatus.CONTROLLER_ABORTED) {
            newJobStatus.reason = controllerAbortedReason;
            newJobStatus.note = controllerAbortedNotes;
            newJobStatus.followUpAction = abortedFollowUpAction;
        } else if (newJobStatus.status === JobStatus.HCP_ABORTED) {
            newJobStatus.reason = hcpAbortedReason;
            newJobStatus.note = hcpAbortedNotes;
            newJobStatus.followUpAction = abortedFollowUpAction;
        }

        jobStatuses.unshift(newJobStatus);

        // Ensure we update currentJobStatus to the new current status
        currentJobStatus = newJobStatus;
    }

    // If the current status is not WITHDRAWN but the job has previously been withdrawn, add a
    // WITHDRAWN status as the previous status to the current one
    if (withdrawnReason && currentJobStatus.status !== JobStatus.WITHDRAWN) {
        jobStatuses.splice(1, 0, {
            date:
                DateTime.fromISO(currentJobStatus.date).minus({ seconds: 1 }).toUTC().toISO() || '',
            status: JobStatus.WITHDRAWN,
            reason: withdrawnReason,
            note: withdrawnNotes,
        });

        if (
            currentJobStatus.status !== JobStatus.CONTROLLER_ABORTED &&
            currentJobStatus.status !== JobStatus.HCP_ABORTED
        ) {
            currentJobStatus.isReverse = true;
        }
    }

    // If the current status is not HCP_ABORTED but the job has previously been aborted by the HCP
    // (and therefore reversed), add a HCP_ABORTED status as the previous status to the current one
    if (hcpAbortedReason && currentJobStatus.status !== JobStatus.HCP_ABORTED) {
        jobStatuses.splice(1, 0, {
            date:
                DateTime.fromISO(currentJobStatus.date).minus({ seconds: 1 }).toUTC().toISO() || '',
            status: JobStatus.HCP_ABORTED,
            reason: hcpAbortedReason,
            note: hcpAbortedNotes,
            followUpAction: abortedFollowUpAction,
        });

        currentJobStatus.isReverse = true;
        currentJobStatus.reason = hcpReverseReason;
        currentJobStatus.note = hcpReverseNotes;
    }

    // If the current status is not CONTROLLER_ABORTED but the job has previously been aborted by
    // the controller (and therefore reversed), add a CONTROLLER_ABORTED status as the previous
    // status to the current one
    if (controllerAbortedReason && currentJobStatus.status !== JobStatus.CONTROLLER_ABORTED) {
        jobStatuses.splice(1, 0, {
            date:
                DateTime.fromISO(currentJobStatus.date).minus({ seconds: 1 }).toUTC().toISO() || '',
            status: JobStatus.CONTROLLER_ABORTED,
            reason: controllerAbortedReason,
            note: controllerAbortedNotes,
            followUpAction: abortedFollowUpAction,
        });

        currentJobStatus.isReverse = true;
        currentJobStatus.reason = controllerReverseReason;
        currentJobStatus.note = controllerReverseNotes;
    }

    // If there's a jobStatusBeforeAbort, and it's not already in the array, add it immediately
    // after the latest aborted status
    if (
        jobStatusBeforeAbort &&
        !jobStatuses.find(({ status }) => status === jobStatusBeforeAbort)
    ) {
        const abortedJob = jobStatuses.find(
            ({ status }) =>
                status === JobStatus.CONTROLLER_ABORTED || status === JobStatus.HCP_ABORTED,
        );
        if (abortedJob) {
            const index = jobStatuses.indexOf(abortedJob);
            jobStatuses.splice(index + 1, 0, {
                date: DateTime.fromISO(abortedJob.date).minus({ seconds: 1 }).toUTC().toISO() || '',
                status: jobStatusBeforeAbort,
            });
        }
    }

    return jobStatuses;
};
